import { ASSETS_CRUD_ACTION_CREATORS } from '../actions/assets_actions';
import RestCrudThunks from './restCrudThunksGenerator';
import { ROLE_TYPES } from '../utils/DataConstants';
import { getBackendUri } from '../utils/EnvConfigUtils';
import { FORBIDDEN_QUERY_PARAM_KEYS, parseJSON } from './fetch_utils';
import { logoutSuccess } from '../actions/session_actions';
import { message } from 'antd';
import FetchUtils from './fetch_utils';
import { retrieveCachedUserDetails } from './session_thunks';
import { collapseHighlightAnalyzerVariants } from '../utils/SearchHighlightUtils';
import { flattenArrayValues, nullSafeGet, nullSafeGetOrElse } from '../utils/DataAccessUtils';
import posthog from 'posthog-js';
import { updateFiltersEntities } from './thunks_wrapper';
import { PM_BULK_ASSET_UPLOAD_TARGET_COLLECTION } from '../reducers/assets_reducer';

const fetchUtils = new FetchUtils();

export const assetsSearchAndAPIAdaptor = (assets) =>
	assets.map(({ source: asset, highlight }) => {
		const normalizedHighlight = highlight
			? collapseHighlightAnalyzerVariants(highlight)
			: highlight;
		const normalized = { ...asset };
		if (!normalized.assetType && normalized.assetTypeName) {
			normalized.assetType = { name: normalized.assetTypeName };
		}
		if (!normalized.location && normalized.locationName) {
			normalized.location = { name: normalized.locationName };
		}
		if (!normalized.warranties && Array.isArray(normalized.serviceWarranties)) {
			normalized.warranties = normalized.serviceWarranties;
		}
		// The search doc carries the parent under `parentDetails` (id + name),
		// but the row display reads `parent.{name,id}` — map it so the
		// "Sub Asset of {parent}" indicator renders in OpenSearch-backed lists.
		if (!normalized.parent && normalized.parentDetails) {
			normalized.parent = normalized.parentDetails;
		}
		if (!normalized.assetLabels && Array.isArray(normalized.labels)) {
			normalized.assetLabels = normalized.labels;
		}
		return {
			...normalized,
			id: parseInt(normalized.id, 10),
			highlight: normalizedHighlight,
		};
	});

export const generateLocationMeterReadingsPDFForBuyer = (id) => {
	const baseUrl = getBackendUri();
	const apiUrl = `/api/v1/buyer/asset/meter_readings/by_location/${id}/generate_pdf`;
	return {
		url: baseUrl + apiUrl,
		httpHeaders: { 'X-Auth-Token': retrieveCachedUserDetails(['token']).token },
	};
};

export const assetsRestCrudThunks = (userType) =>
	userType === ROLE_TYPES.SUPPLIER ? assetsRestCrudThunksForSupplier : assetsRestCrudThunksForBuyer;

export const assetsRestCrudThunksForBuyer = new RestCrudThunks(
	'assets',
	'asset',
	'asset',
	'assets',
	ASSETS_CRUD_ACTION_CREATORS,
	null,
	ROLE_TYPES.BUYER,
	false,
	'asset/'
);
export const assetsRestCrudThunksForSupplier = new RestCrudThunks(
	'assets',
	'asset',
	'asset',
	'assets',
	ASSETS_CRUD_ACTION_CREATORS,
	null,
	ROLE_TYPES.SUPPLIER,
	false,
	'asset/'
);

export const assetsRestReadThunk = (
	userType,
	params,
	targetCollectionName = null,
	pagination = null,
	sorting = null,
	filters = null,
	addToTargetCollection = false
) => {
	const searchBody = flattenArrayValues({ ...(params || {}), ...(filters || {}) });
	// A lookup by an explicit id list wants every one of those records, not a page of them. Neither
	// endpoint infers that: with no pagination the backend applies its own default limit of 10
	// (PaginationUtil.getPagination), so a caller resolving 50 preselected ids would silently get 10.
	// This must be applied to BOTH branches — passing it only to the OpenSearch one left every
	// customer on the REST path with truncated `fetchMultiple` results.
	let searchPagination = pagination;
	const identifierParam = params && (params.ids || params.buyerSpecificAssetIdOrIds);
	if (!searchPagination && identifierParam) {
		const idCount = Array.isArray(identifierParam)
			? identifierParam.length
			: String(identifierParam).split(',').length;
		searchPagination = { current: 1, pageSize: idCount };
	}
	return posthog.isFeatureEnabled('assets-openwsearch')
		? assetsRestCrudThunks(userType).searchWithPostMethod(
				searchBody,
				targetCollectionName,
				searchPagination,
				sorting,
				filters,
				addToTargetCollection,
				assetsSearchAndAPIAdaptor
		  )
		: assetsRestCrudThunks(userType).read(
				params,
				targetCollectionName,
				searchPagination,
				sorting,
				filters,
				addToTargetCollection
		  );
};

// Budget for the identifier list inside one request URL, in encoded characters. Chunking is by
// LENGTH, not by count: identifiers here are free text (buyer-specific asset ids), so a fixed
// "200 per request" is safe for 8-character numeric ids and blows past the ~8KB URL limit for long
// ones. Left well under 8KB to leave room for the base URL, role prefix and the other params.
const ASSET_IDENTIFIER_QUERY_BUDGET = 6000;

// Only the non-OpenSearch branch puts the list in the query string — the OpenSearch branch POSTs a
// body and has no such limit — but chunking on the safe assumption costs one extra round trip at
// worst and avoids a failure that only shows up for customers on one side of the flag.
export const chunkIdentifiersForQuery = (
	identifiers: any[],
	budget: number = ASSET_IDENTIFIER_QUERY_BUDGET
): string[][] => {
	const chunks: string[][] = [];
	let currentChunk: string[] = [];
	let currentLength = 0;

	(identifiers || []).forEach((rawIdentifier) => {
		const identifier = String(rawIdentifier);
		// +3 for the separator, which encodes to "%2C".
		const encodedCost = encodeURIComponent(identifier).length + 3;
		if (currentChunk.length > 0 && currentLength + encodedCost > budget) {
			chunks.push(currentChunk);
			currentChunk = [];
			currentLength = 0;
		}
		currentChunk.push(identifier);
		currentLength += encodedCost;
	});

	if (currentChunk.length > 0) {
		chunks.push(currentChunk);
	}
	return chunks;
};

/**
 * Reads assets by a list of identifiers, split across as many requests as the URL budget needs.
 *
 * Chunks are issued SEQUENTIALLY. `read` buckets its takeLatest cancellation by target collection,
 * so parallel chunks against one collection would cancel each other and only the last would resolve.
 * Every chunk after the first also appends rather than replaces (`addToTargetCollection`), or each
 * would overwrite the previous one's records in the collection. `appendToTargetCollection` extends
 * that to the first chunk too, for callers topping up a collection that already holds records.
 */
const readAssetsByIdentifierChunks =
	(
		roleType,
		filterKey: string,
		identifiers: any[],
		targetCollectionName = null,
		appendToTargetCollection = false
	) =>
	(dispatch) => {
		const chunks = chunkIdentifiersForQuery(identifiers);
		if (chunks.length === 0) {
			return Promise.resolve([]);
		}

		return chunks.reduce(
			(previousChunks, chunk, chunkIndex) =>
				previousChunks.then((collectedAssets) =>
					dispatch(
						assetsRestReadThunk(
							roleType,
							// noActiveFilter matches the PM asset picker, which also lets inactive assets be
							// scheduled — a lookup should not silently drop an asset the picker would offer.
							{ [filterKey]: chunk, noActiveFilter: true },
							targetCollectionName,
							null,
							null,
							null,
							appendToTargetCollection || chunkIndex > 0
						)
					).then((assets) => collectedAssets.concat(assets || []))
				),
			Promise.resolve([])
		);
	};

/**
 * Resolves a user-supplied list of Asset IDs to asset records.
 *
 * An "Asset ID" here is whatever the app shows the user for that value — `AssetIdDisplay` renders
 * `buyerSpecificAssetId || id` — so a single list can freely mix both forms, which is exactly what
 * happens when a customer exports from one system and uploads here. The backend matches both in one
 * predicate (`buyerSpecificAssetIdOrIds`, buyer-specific winning on a tie), so there is no need to
 * guess which kind an identifier is.
 *
 * Resolves with the flat list of matched assets. Callers diff it against what they submitted to find
 * the unmatched identifiers — a match is an asset whose `id` or `buyerSpecificAssetId` equals the
 * identifier, compared case-insensitively.
 */
export const resolveAssetIdentifiers = (roleType) => (identifiers) => (dispatch) => {
	const cleanedIdentifiers = (identifiers || [])
		.map((identifier) => String(identifier).trim())
		.filter((identifier) => identifier.length > 0);

	return dispatch(
		readAssetsByIdentifierChunks(
			roleType,
			'buyerSpecificAssetIdOrIds',
			cleanedIdentifiers,
			PM_BULK_ASSET_UPLOAD_TARGET_COLLECTION
		)
	);
};

/**
 * Reads assets by OpenWrench id. Same chunking as above — a schedule can carry hundreds of assets,
 * and both the "Download Selected" export and the async-select label resolution hand this the whole
 * set at once.
 */
export const readAssetsByIds =
	(roleType) =>
	(ids, targetCollectionName = null, appendToTargetCollection = false) =>
	(dispatch) =>
		dispatch(
			readAssetsByIdentifierChunks(
				roleType,
				'ids',
				ids,
				targetCollectionName,
				appendToTargetCollection
			)
		);

export const assetsFetchDataForRole = (roleType) => {
	const thunks = assetsRestCrudThunks(roleType);
	return posthog.isFeatureEnabled('assets-openwsearch')
		? {
				...thunks,
				read: (params, targetCollectionName, pagination, sorting, filters, addToTargetCollection) =>
					assetsRestReadThunk(
						roleType,
						params,
						targetCollectionName,
						pagination,
						sorting,
						filters,
						addToTargetCollection
					),
		  }
		: thunks;
};

export const applyAssetsFiltersForRole = (roleType) => (history, targetCollectionName, filters) =>
	updateFiltersEntities(roleType, 'assets', assetsFetchDataForRole(roleType), null)(
		true,
		history,
		targetCollectionName,
		filters,
		undefined
	);

// Link existing assets as sub assets (children) of `parentAssetId` (OPE-4959).
// Rejects with the backend's error message so the UI can surface the reason.
export const linkSubAsset =
	(userType) =>
	(parentAssetId, assetIds = []) => {
		const baseUrl = getBackendUri();
		const apiUrl = `/api/v1/${userType}/asset/assets/${parentAssetId}/link_sub_assets`;
		return (dispatch) => {
			return new Promise((resolve, reject) => {
				fetchUtils
					.post(baseUrl + apiUrl, {
						data: { assetIds },
					})
					.then(parseJSON)
					.then(({ json, status }) => {
						if (status === 200) {
							resolve(json.data);
						} else {
							reject(nullSafeGetOrElse('message', json, 'Failed to link sub assets'));
						}
					})
					.catch((error) => {
						reject(error.message);
					});
			});
		};
	};

export const linkSubAssetForBuyer = (parentAssetId, assetIds) =>
	linkSubAsset(ROLE_TYPES.BUYER)(parentAssetId, assetIds);
export const linkSubAssetForSupplier = (parentAssetId, assetIds) =>
	linkSubAsset(ROLE_TYPES.SUPPLIER)(parentAssetId, assetIds);

// Unlink sub assets from `parentAssetId` (clears their parentId).
// Rejects with the backend's error message so the UI can surface the reason.
export const unlinkSubAsset =
	(userType) =>
	(parentAssetId, assetIds = []) => {
		const baseUrl = getBackendUri();
		const apiUrl = `/api/v1/${userType}/asset/assets/${parentAssetId}/unlink_sub_assets`;
		return (dispatch) => {
			return new Promise((resolve, reject) => {
				fetchUtils
					.post(baseUrl + apiUrl, {
						data: { assetIds },
					})
					.then(parseJSON)
					.then(({ json, status }) => {
						if (status === 200) {
							resolve(json.data);
						} else {
							reject(nullSafeGetOrElse('message', json, 'Failed to unlink sub assets'));
						}
					})
					.catch((error) => {
						reject(error.message);
					});
			});
		};
	};

export const unlinkSubAssetForBuyer = (parentAssetId, assetIds) =>
	unlinkSubAsset(ROLE_TYPES.BUYER)(parentAssetId, assetIds);
export const unlinkSubAssetForSupplier = (parentAssetId, assetIds) =>
	unlinkSubAsset(ROLE_TYPES.SUPPLIER)(parentAssetId, assetIds);

export const assetsRestCrudThunksForDowntime = new RestCrudThunks(
	'assets',
	'asset',
	'asset',
	'assets',
	ASSETS_CRUD_ACTION_CREATORS,
	null,
	ROLE_TYPES.BUYER,
	false,
	'asset/asset_downtimes/analytics/'
);
export const assetsRestCrudThunksForDowntimeForSupplier = new RestCrudThunks(
	'assets',
	'asset',
	'asset',
	'assets',
	ASSETS_CRUD_ACTION_CREATORS,
	null,
	ROLE_TYPES.SUPPLIER,
	false,
	'asset/asset_downtimes/analytics/'
);

export const assetsRestCrudThunksForReadyForReplacement = new RestCrudThunks(
	'assets/ready_for_replacement',
	'asset',
	'asset',
	'assets',
	ASSETS_CRUD_ACTION_CREATORS,
	null,
	ROLE_TYPES.BUYER,
	false,
	'asset/'
);
export const assetsRestCrudThunksForReadyForReplacementForSupplier = new RestCrudThunks(
	'assets/ready_for_replacement',
	'asset',
	'asset',
	'assets',
	ASSETS_CRUD_ACTION_CREATORS,
	null,
	ROLE_TYPES.SUPPLIER,
	false,
	'asset/'
);

// Filters go in the POST body, mirroring `searchWithPostMethod`. As a query string they blew past the
// server's request-line limit whenever the filter set was large (a location or asset-type filter holding
// hundreds of ids), and the export came back with the wrong rows or none at all.
//
// Pagination and sorting stay in the query string: the backend reads `sort_by` / `order` off the query
// string only (`PaginationUtil.getPagination`), and `fetchUtils.post` builds no query string of its own.
//
// The backend stores a body-sent filter set once and hands the report generator a short reference to it,
// so the per-page download URLs it fetches stay a fixed size no matter how big the filter set is.
const downloadAssetCSV =
	(roleType) =>
	(params, filters = null) => {
		const baseUrl = getBackendUri();
		const apiUrl = `/api/v1/${roleType}/asset/assets/csv_report_generator`;
		const queryParamsString = fetchUtils.getQueryString(
			fetchUtils.sanitize(params || {}, FORBIDDEN_QUERY_PARAM_KEYS)
		);
		const url = baseUrl + apiUrl + (queryParamsString ? `?${queryParamsString}` : '');
		// The index page's filter bag carries the backlink keys it picked up from its own URL. A GET
		// dropped those before they reached the backend, so drop them here too rather than caching them
		// alongside the real filters.
		const body = fetchUtils.sanitize(filters || {}, FORBIDDEN_QUERY_PARAM_KEYS);

		return (dispatch) => {
			return new Promise((resolve, reject) => {
				fetchUtils
					.post(url, {
						headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
						data: body,
					})
					.then(parseJSON)
					.then(({ json, status, ok }) => {
						const d = json;
						if (status === 401) {
							dispatch(logoutSuccess());
						}
						if (d.status === 'error') {
							reject(d.message);
							message.error(d.data);
						} else {
							resolve(d.data);
							const hide = message.loading(d.data, 0);
							setTimeout(hide, 7000);
						}
					})
					.catch((d) => {
						reject(d.message);
					});
			});
		};
	};

const createAssetFromEquipmentPerStockLocationBulk = (roleType) => (entities) => {
	const baseUrl = getBackendUri();
	const apiUrl = `/api/v1/${roleType}/asset/assets/from_equipment_per_stock_location/bulk`;

	// if sorting present, fix order naming and add to query params

	return (dispatch) => {
		return new Promise((resolve, reject) => {
			dispatch(ASSETS_CRUD_ACTION_CREATORS.createStart({}));

			fetchUtils
				.post(
					baseUrl + apiUrl,
					Object.assign({
						headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
						data: entities,
					})
				)
				.then(parseJSON)
				.then(({ json, status, ok }) => {
					const d = json;
					if (status === 401) {
						dispatch(logoutSuccess());
					}
					if (d.status === 'error') {
						dispatch(ASSETS_CRUD_ACTION_CREATORS.createError(d.message, {}));
						reject(d.message);
					} else {
						dispatch(ASSETS_CRUD_ACTION_CREATORS.createSuccess({}));
						resolve(d.data);
					}
				})
				.catch((d) => {
					dispatch(ASSETS_CRUD_ACTION_CREATORS.createError(d.message, {}));
					reject(d.message);
				});
		});
	};
};

export const downloadAssetCSVForBuyer = downloadAssetCSV(ROLE_TYPES.BUYER);
export const downloadAssetCSVForSupplier = downloadAssetCSV(ROLE_TYPES.SUPPLIER);

export const createAssetFromEquipmentPerStockLocationBulkForSupplier =
	createAssetFromEquipmentPerStockLocationBulk(ROLE_TYPES.SUPPLIER);

const createAssetFromEquipmentTypeBulk = (roleType) => (entities) => {
	const baseUrl = getBackendUri();
	const apiUrl = `/api/v1/${roleType}/asset/assets/from_equipment_type_and_stock_location/bulk`;

	// if sorting present, fix order naming and add to query params

	return (dispatch) => {
		return new Promise((resolve, reject) => {
			dispatch(ASSETS_CRUD_ACTION_CREATORS.createStart({}));

			fetchUtils
				.post(
					baseUrl + apiUrl,
					Object.assign({
						headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
						data: entities,
					})
				)
				.then(parseJSON)
				.then(({ json, status, ok }) => {
					const d = json;
					if (status === 401) {
						dispatch(logoutSuccess());
					}
					if (d.status === 'error') {
						dispatch(ASSETS_CRUD_ACTION_CREATORS.createError(d.message, {}));
						reject(d.message);
					} else {
						dispatch(ASSETS_CRUD_ACTION_CREATORS.createSuccess({}));
						resolve(d.data);
					}
				})
				.catch((d) => {
					dispatch(ASSETS_CRUD_ACTION_CREATORS.createError(d.message, {}));
					reject(d.message);
				});
		});
	};
};

export const createAssetFromEquipmentTypeBulkForSupplier = createAssetFromEquipmentTypeBulk(
	ROLE_TYPES.SUPPLIER
);

const fetchAssociatedAsset = (roleType) => (epslId) => {
	const baseUrl = getBackendUri();
	const apiUrl = `/api/v1/${roleType}/asset/assets/by_equipment_per_stock_location_id/${epslId}`;

	// if sorting present, fix order naming and add to query params

	return (dispatch) => {
		return new Promise((resolve, reject) => {
			dispatch(ASSETS_CRUD_ACTION_CREATORS.fetchStart());

			fetchUtils
				.get(baseUrl + apiUrl, {})
				.then(parseJSON)
				.then(({ json, status, ok }) => {
					const d = json;
					if (status === 401) {
						dispatch(logoutSuccess());
					}
					if (d.status === 'error') {
						dispatch(ASSETS_CRUD_ACTION_CREATORS.fetchError(d.message, ''));
						reject(d.message);
					} else {
						dispatch(ASSETS_CRUD_ACTION_CREATORS.fetchOneSuccess(d.data));
						resolve(d.data);
					}
				})
				.catch((d) => {
					dispatch(ASSETS_CRUD_ACTION_CREATORS.fetchError(d.message, ''));
					reject(d.message);
				});
		});
	};
};

export const fetchAssociatedAssetOfEquipmentPerStockLocationForSupplier = fetchAssociatedAsset(
	ROLE_TYPES.SUPPLIER
);

const fetchAssetsGroupedByLocation =
	(roleType) =>
	(
		params,
		targetCollectionName = null,
		pagination = null,
		sorting = null,
		filters = null,
		addToTargetCollection = false
	) => {
		// if pagination present, transform into limit/offset format and add to query params
		if (pagination) {
			const paginationQuery = {
				offset: (pagination.current - 1) * pagination.pageSize,
				limit: pagination.pageSize,
			};
			params = {
				...params,
				...paginationQuery,
			};
		}

		// if sorting present, fix order naming and add to query params
		if (sorting) {
			const sortingQuery = { ...sorting, order: sorting.order === 'ascend' ? 'asc' : 'desc' };
			params = {
				...params,
				...sortingQuery,
			};
		}

		if (filters) {
			params = {
				...params,
				...filters,
			};
		}

		return (dispatch) => {
			return new Promise((resolve, reject) => {
				dispatch(ASSETS_CRUD_ACTION_CREATORS.fetchStart());

				const suffixUrl = '/asset/assets/by_grouped_location';
				fetchUtils
					.get(
						getBackendUri() + `/api/v1/${roleType}` + suffixUrl,
						Object.assign(
							{ headers: { Accept: 'application/json', 'Content-Type': 'application/json' } },
							params
						)
					)
					.then(parseJSON)
					.then(({ json, status, ok }) => {
						const d = json;
						if (status === 401) {
							dispatch(logoutSuccess());
						}
						if (d.status === 'error') {
							dispatch(ASSETS_CRUD_ACTION_CREATORS.fetchError(d.message, ''));
							reject(d.message);
						} else {
							dispatch(
								ASSETS_CRUD_ACTION_CREATORS.fetchSuccess(
									d.data,
									d.count,
									targetCollectionName,
									{
										...pagination,
										total: d.count,
									},
									sorting,
									filters,
									addToTargetCollection
								)
							);
							resolve(d.data);
						}
					})
					.catch((d) => {
						dispatch(ASSETS_CRUD_ACTION_CREATORS.fetchError(d.message, ''));
						reject(d.message);
					});
			});
		};
	};

export const fetchAssetsGroupedByLocationForBuyer = fetchAssetsGroupedByLocation(ROLE_TYPES.BUYER);
export const fetchAssetsGroupedByLocationForSupplier = fetchAssetsGroupedByLocation(
	ROLE_TYPES.SUPPLIER
);

const updateAssetImpairments = (roleType) => (data) => {
	const baseUrl = getBackendUri();
	const apiUrl = `/api/v1/${roleType}/asset/assets/update_impairments`;

	// if sorting present, fix order naming and add to query params

	return (dispatch) => {
		return new Promise((resolve, reject) => {
			dispatch(ASSETS_CRUD_ACTION_CREATORS.updateStart({}));

			fetchUtils
				.put(
					baseUrl + apiUrl,
					Object.assign({
						headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
						data,
					})
				)
				.then(parseJSON)
				.then(({ json, status, ok }) => {
					const d = json;
					if (status === 401) {
						dispatch(logoutSuccess());
					}
					if (d.status === 'error') {
						dispatch(ASSETS_CRUD_ACTION_CREATORS.updateError(d.message, {}));
						reject(d.message);
					} else {
						dispatch(ASSETS_CRUD_ACTION_CREATORS.updateSuccess(d.data));
						resolve(d.data);
					}
				})
				.catch((d) => {
					dispatch(ASSETS_CRUD_ACTION_CREATORS.updateError(d.message, {}));
					reject(d.message);
				});
		});
	};
};

export const updateAssetImpairmentsForBuyer = updateAssetImpairments(ROLE_TYPES.BUYER);
export const updateAssetImpairmentsForSupplier = updateAssetImpairments(ROLE_TYPES.SUPPLIER);

const addImpairmentsToJournal = (roleType) => (data) => {
	const baseUrl = getBackendUri();
	const apiUrl = `/api/v1/${roleType}/asset/assets/${data.id}/add_impairments_to_journal`;

	// if sorting present, fix order naming and add to query params

	return (dispatch) => {
		return new Promise((resolve, reject) => {
			dispatch(ASSETS_CRUD_ACTION_CREATORS.updateStart({}));

			fetchUtils
				.put(
					baseUrl + apiUrl,
					Object.assign({
						headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
						data,
					})
				)
				.then(parseJSON)
				.then(({ json, status, ok }) => {
					const d = json;
					if (status === 401) {
						dispatch(logoutSuccess());
					}
					if (d.status === 'error') {
						dispatch(ASSETS_CRUD_ACTION_CREATORS.updateError(d.message, {}));
						reject(d.message);
					} else {
						dispatch(ASSETS_CRUD_ACTION_CREATORS.updateSuccess(d.data));
						resolve(d.data);
					}
				})
				.catch((d) => {
					dispatch(ASSETS_CRUD_ACTION_CREATORS.updateError(d.message, {}));
					reject(d.message);
				});
		});
	};
};

export const addImpairmentsToJournalForBuyer = addImpairmentsToJournal(ROLE_TYPES.BUYER);
export const addImpairmentsToJournalForSupplier = addImpairmentsToJournal(ROLE_TYPES.SUPPLIER);

export const assetAudits = (roleType) => (params) => {
	const baseUrl = getBackendUri();
	const apiUrl = `/api/v1/${roleType}/asset/asset_audits`;

	return (dispatch) => {
		return new Promise((resolve, reject) => {
			fetchUtils
				.get(baseUrl + apiUrl, params)
				.then(parseJSON)
				.then(({ json, status, ok }) => {
					const d = json;
					if (status === 401) {
						dispatch(logoutSuccess());
					}
					if (d.status === 'error') {
						reject(d.message);
					} else {
						resolve(d.data);
					}
				})
				.catch((d) => {
					reject(d.message);
				});
		});
	};
};

export const assetAuditForBuyer = assetAudits(ROLE_TYPES.BUYER);
export const assetAuditForSupplier = assetAudits(ROLE_TYPES.SUPPLIER);

export const assetDowntime = (roleType) => (data) => {
	const baseUrl = getBackendUri();
	const apiUrl = `/api/v1/${roleType}/asset/asset_downtimes`;
	return (dispatch) => {
		return new Promise((resolve, reject) => {
			//dispatch(ASSETS_CRUD_ACTION_CREATORS.updateStart({}));

			fetchUtils
				.post(
					baseUrl + apiUrl,
					Object.assign({
						data: data,
					})
				)
				.then(parseJSON)
				.then(({ json, status, ok }) => {
					const d = json;
					if (status === 401) {
						dispatch(logoutSuccess());
					}
					if (d.status === 'error') {
						//dispatch(ASSETS_CRUD_ACTION_CREATORS.updateError(d.message, {}));
						reject(d.message);
					} else {
						//dispatch(ASSETS_CRUD_ACTION_CREATORS.updateSuccess(d.data));
						resolve(d.data);
					}
				})
				.catch((d) => {
					//dispatch(ASSETS_CRUD_ACTION_CREATORS.updateError(d.message, {}));
					reject(d.message);
				});
		});
	};
};
export const assetDowntimeUpdate = (roleType) => (data) => {
	const baseUrl = getBackendUri();
	const apiUrl = `/api/v1/${roleType}/asset/asset_downtimes`;
	return (dispatch) => {
		return new Promise((resolve, reject) => {
			// dispatch(ASSETS_CRUD_ACTION_CREATORS.updateStart({}));

			fetchUtils
				.put(
					baseUrl + apiUrl,
					Object.assign({
						data: data,
					})
				)
				.then(parseJSON)
				.then(({ json, status, ok }) => {
					const d = json;
					if (status === 401) {
						dispatch(logoutSuccess());
					}
					if (d.status === 'error') {
						// dispatch(ASSETS_CRUD_ACTION_CREATORS.updateError(d.message, {}));
						reject(d.message);
					} else {
						// dispatch(ASSETS_CRUD_ACTION_CREATORS.updateSuccess(d.data));
						resolve(d.data);
					}
				})
				.catch((d) => {
					// dispatch(ASSETS_CRUD_ACTION_CREATORS.updateError(d.message, {}));
					reject(d.message);
				});
		});
	};
};

export const assetDowntimeGet = (roleType) => (params) => {
	const baseUrl = getBackendUri();
	const apiUrl = `/api/v1/${roleType}/asset/asset_downtimes`;
	return (dispatch) => {
		return new Promise((resolve, reject) => {
			fetchUtils
				.get(baseUrl + apiUrl, params)
				.then(parseJSON)
				.then(({ json, status, ok }) => {
					const d = json;
					if (status === 401) {
						dispatch(logoutSuccess());
					}
					if (d.status === 'error') {
						reject(d.message);
					} else {
						resolve(d.data);
					}
				})
				.catch((d) => {
					reject(d.message);
				});
		});
	};
};

export const assetDowntimeForBuyer = assetDowntime(ROLE_TYPES.BUYER);
export const assetDowntimeForSupplier = assetDowntime(ROLE_TYPES.SUPPLIER);

// ANALYTICS API CALLS

export const assetDowntimeMDRGet = (roleType) => (params) => {
	const baseUrl = getBackendUri();
	const apiUrl = `/api/v1/${roleType}/asset/asset_downtimes/mean_downtime_report`;
	return (dispatch) => {
		return new Promise((resolve, reject) => {
			fetchUtils
				.get(baseUrl + apiUrl, params)
				.then(parseJSON)
				.then(({ json, status, ok }) => {
					const d = json;
					if (status === 401) {
						dispatch(logoutSuccess());
					}
					if (d.status === 'error') {
						reject(d.message);
					} else {
						resolve(d.data);
					}
				})
				.catch((d) => {
					reject(d.message);
				});
		});
	};
};

export const assetDowntimeMTTRGet = (roleType) => (params) => {
	const baseUrl = getBackendUri();
	const apiUrl = `/api/v1/${roleType}/asset/asset_downtimes/mean_time_to_respond`;
	return (dispatch) => {
		return new Promise((resolve, reject) => {
			fetchUtils
				.get(baseUrl + apiUrl, params)
				.then(parseJSON)
				.then(({ json, status, ok }) => {
					const d = json;
					if (status === 401) {
						dispatch(logoutSuccess());
					}
					if (d.status === 'error') {
						reject(d.message);
					} else {
						resolve(d.data);
					}
				})
				.catch((d) => {
					reject(d.message);
				});
		});
	};
};

export const assetDowntimeMTBFGet = (roleType) => (params) => {
	const baseUrl = getBackendUri();
	const apiUrl = `/api/v1/${roleType}/asset/asset_downtimes/mean_time_between_failures`;
	return (dispatch) => {
		return new Promise((resolve, reject) => {
			fetchUtils
				.get(baseUrl + apiUrl, params)
				.then(parseJSON)
				.then(({ json, status, ok }) => {
					const d = json;
					if (status === 401) {
						dispatch(logoutSuccess());
					}
					if (d.status === 'error') {
						reject(d.message);
					} else {
						resolve(d.data);
					}
				})
				.catch((d) => {
					reject(d.message);
				});
		});
	};
};

export const assetRepairReplaceScoreGet = (roleType) => (assetId) => {
	const baseUrl = getBackendUri();
	const apiUrl = `/api/v1/${roleType}/asset/assets/${assetId}/repair_replace_score`;
	return (dispatch) => {
		return new Promise((resolve, reject) => {
			fetchUtils
				.get(baseUrl + apiUrl, {})
				.then(parseJSON)
				.then(({ json, status, ok }) => {
					const d = json;
					if (status === 401) {
						dispatch(logoutSuccess());
					}
					if (d.status === 'error') {
						reject(d.message);
					} else {
						resolve(d.data);
					}
				})
				.catch((d) => {
					reject(d.message);
				});
		});
	};
};

export const assetDeepAnalysisHistoryGet = (roleType) => (assetId) => {
	const baseUrl = getBackendUri();
	const apiUrl = `/api/v1/${roleType}/asset/assets/${assetId}/deep_analysis`;
	return (dispatch) => {
		return new Promise((resolve, reject) => {
			fetchUtils
				.get(baseUrl + apiUrl, {})
				.then(parseJSON)
				.then(({ json, status }) => {
					const d = json;
					if (status === 401) {
						dispatch(logoutSuccess());
					}
					if (d.status === 'error') {
						reject(d.message);
					} else {
						resolve(d.data);
					}
				})
				.catch((error) => {
					reject(error.message);
				});
		});
	};
};

export const assetDeepAnalysisCreate = (roleType) => (assetId) => {
	const baseUrl = getBackendUri();
	const apiUrl = `/api/v1/${roleType}/asset/assets/${assetId}/deep_analysis`;
	return (dispatch) => {
		return new Promise((resolve, reject) => {
			fetchUtils
				.post(
					baseUrl + apiUrl,
					Object.assign({
						headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
						data: {},
					})
				)
				.then(parseJSON)
				.then(({ json, status }) => {
					const d = json;
					if (status === 401) {
						dispatch(logoutSuccess());
					}
					if (d.status === 'error') {
						reject(d.message);
					} else {
						resolve(d.data);
					}
				})
				.catch((error) => {
					reject(error.message);
				});
		});
	};
};

export const downloadDowntimeAssetCSV =
	(roleType) =>
	(params, filters = null) => {
		const baseUrl = getBackendUri();
		const apiUrl = `/api/v1/${roleType}/asset/asset_downtimes/analytics/assets/csv_report_generator`;

		// if sorting present, fix order naming and add to query params

		if (filters) {
			params = {
				...params,
				...filters,
			};
		}

		return (dispatch) => {
			return new Promise((resolve, reject) => {
				fetchUtils
					.get(
						baseUrl + apiUrl,
						Object.assign(
							{ headers: { Accept: 'application/json', 'Content-Type': 'application/json' } },
							params
						)
					)
					.then(parseJSON)
					.then(({ json, status, ok }) => {
						const d = json;
						if (status === 401) {
							dispatch(logoutSuccess());
						}
						if (d.status === 'error') {
							reject(d.message);
							message.error(d.data);
						} else {
							resolve(d.data);
							const hide = message.loading(d.data, 0);
							setTimeout(hide, 7000);
						}
					})
					.catch((d) => {
						reject(d.message);
					});
			});
		};
	};

const updateAssetsBulk = (roleType) => (data) => {
	const baseUrl = getBackendUri();
	const apiUrl = `/api/v1/${roleType}/asset/assets/bulk`;

	// if sorting present, fix order naming and add to query params

	return (dispatch) => {
		return new Promise((resolve, reject) => {
			dispatch(ASSETS_CRUD_ACTION_CREATORS.updateStart({}));

			fetchUtils
				.put(
					baseUrl + apiUrl,
					Object.assign({
						headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
						data,
					})
				)
				.then(parseJSON)
				.then(({ json, status, ok }) => {
					const d = json;
					if (status === 401) {
						dispatch(logoutSuccess());
					}
					if (d.status === 'error') {
						dispatch(ASSETS_CRUD_ACTION_CREATORS.updateError(d.message, {}));
						reject(d.message);
					} else {
						dispatch(ASSETS_CRUD_ACTION_CREATORS.updateSuccess(d.data));
						resolve(d.data);
					}
				})
				.catch((d) => {
					dispatch(ASSETS_CRUD_ACTION_CREATORS.updateError(d.message, {}));
					reject(d.message);
				});
		});
	};
};

export const updateAssetsBulkForSupplier = updateAssetsBulk(ROLE_TYPES.SUPPLIER);

const bulkTransferAssetsToLocations = (roleType) => (data) => {
	const baseUrl = getBackendUri();
	const apiUrl = `/api/v1/${roleType}/asset/assets/bulk_transfer/locations`;

	return (dispatch) => {
		return new Promise((resolve, reject) => {
			dispatch(ASSETS_CRUD_ACTION_CREATORS.updateStart({}));

			fetchUtils
				.post(
					baseUrl + apiUrl,
					Object.assign({
						headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
						data,
					})
				)
				.then(parseJSON)
				.then(({ json, status, ok }) => {
					const d = json;
					if (status === 401) {
						dispatch(logoutSuccess());
					}
					if (d.status === 'error') {
						dispatch(ASSETS_CRUD_ACTION_CREATORS.updateError(d.message, {}));
						reject(d.message);
					} else {
						dispatch(ASSETS_CRUD_ACTION_CREATORS.updateSuccess(d.data));
						resolve(d.data);
					}
				})
				.catch((d) => {
					dispatch(ASSETS_CRUD_ACTION_CREATORS.updateError(d.message, {}));
					reject(d.message);
				});
		});
	};
};

export const bulkTransferAssetsToLocationsForSupplier = bulkTransferAssetsToLocations(
	ROLE_TYPES.SUPPLIER
);

export const scheduleAssetTransfer = (roleType) => (data) => {
	const baseUrl = getBackendUri();
	const apiUrl = `/api/v1/${roleType}/asset/assets/scheduled_transfer`;

	return (dispatch) => {
		return new Promise((resolve, reject) => {
			dispatch(ASSETS_CRUD_ACTION_CREATORS.updateStart({}));

			fetchUtils
				.post(
					baseUrl + apiUrl,
					Object.assign({
						headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
						data,
					})
				)
				.then(parseJSON)
				.then(({ json, status, ok }) => {
					const d = json;
					if (status === 401) {
						dispatch(logoutSuccess());
					}
					if (d.status === 'error') {
						dispatch(ASSETS_CRUD_ACTION_CREATORS.updateError(d.message, {}));
						reject(d.message);
					} else {
						dispatch(ASSETS_CRUD_ACTION_CREATORS.updateSuccess(d.data));
						resolve(d.data);
					}
				})
				.catch((d) => {
					dispatch(ASSETS_CRUD_ACTION_CREATORS.updateError(d.message, {}));
					reject(d.message);
				});
		});
	};
};

export const cancelScheduledAssetTransfer = (roleType) => (data) => {
	const baseUrl = getBackendUri();
	const apiUrl = `/api/v1/${roleType}/asset/assets/scheduled_transfer/cancel`;

	return (dispatch) => {
		return new Promise((resolve, reject) => {
			dispatch(ASSETS_CRUD_ACTION_CREATORS.updateStart({}));

			fetchUtils
				.post(
					baseUrl + apiUrl,
					Object.assign({
						headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
						data,
					})
				)
				.then(parseJSON)
				.then(({ json, status }) => {
					if (status === 401) {
						dispatch(logoutSuccess());
					}
					if (json.status === 'error') {
						dispatch(ASSETS_CRUD_ACTION_CREATORS.updateError(json.message, {}));
						reject(json.message);
					} else {
						dispatch(ASSETS_CRUD_ACTION_CREATORS.updateSuccess(json.data));
						resolve(json.data);
					}
				})
				.catch((d) => {
					dispatch(ASSETS_CRUD_ACTION_CREATORS.updateError(d.message, {}));
					reject(d.message);
				});
		});
	};
};

export const updateScheduledAssetTransfer = (roleType) => (data) => {
	const baseUrl = getBackendUri();
	const apiUrl = `/api/v1/${roleType}/asset/assets/scheduled_transfer`;

	return (dispatch) => {
		return new Promise((resolve, reject) => {
			dispatch(ASSETS_CRUD_ACTION_CREATORS.updateStart({}));

			fetchUtils
				.put(
					baseUrl + apiUrl,
					Object.assign({
						headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
						data,
					})
				)
				.then(parseJSON)
				.then(({ json, status }) => {
					if (status === 401) {
						dispatch(logoutSuccess());
					}
					if (json.status === 'error') {
						dispatch(ASSETS_CRUD_ACTION_CREATORS.updateError(json.message, {}));
						reject(json.message);
					} else {
						dispatch(ASSETS_CRUD_ACTION_CREATORS.updateSuccess(json.data));
						resolve(json.data);
					}
				})
				.catch((d) => {
					dispatch(ASSETS_CRUD_ACTION_CREATORS.updateError(d.message, {}));
					reject(d.message);
				});
		});
	};
};

const getAssetsBulkTransferStatus = (roleType) => () => {
	const baseUrl = getBackendUri();
	const apiUrl = `/api/v1/${roleType}/asset/assets/bulk_transfer/locations/status`;

	return (dispatch) => {
		return new Promise((resolve, reject) => {
			fetchUtils
				.get(
					baseUrl + apiUrl,
					Object.assign({
						headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
					})
				)
				.then(parseJSON)
				.then(({ json, status, ok }) => {
					const d = json;
					if (status === 401) {
						dispatch(logoutSuccess());
					}
					if (d.status === 'error') {
						reject(d.message);
						message.error(d.data);
					} else {
						resolve(d.data);
					}
				})
				.catch((d) => {
					reject(d.message);
				});
		});
	};
};

export const getAssetsBulkTransferStatusForSupplier = getAssetsBulkTransferStatus(
	ROLE_TYPES.SUPPLIER
);

const moveAssetToLocation = (roleType) => (assetId, data) => {
	const baseUrl = getBackendUri();
	const apiUrl = `/api/v1/${roleType}/asset/assets/${assetId}/transfer_to_location`;

	// if sorting present, fix order naming and add to query params

	return (dispatch) => {
		const entity = { id: assetId };
		return new Promise((resolve, reject) => {
			dispatch(ASSETS_CRUD_ACTION_CREATORS.updateStart(entity));

			fetchUtils
				.post(
					baseUrl + apiUrl,
					Object.assign({
						headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
						data,
					})
				)
				.then(parseJSON)
				.then(({ json, status, ok }) => {
					const d = json;
					if (status === 401) {
						dispatch(logoutSuccess());
					}
					if (d.status === 'error') {
						dispatch(ASSETS_CRUD_ACTION_CREATORS.updateError(d.message, entity));
						reject(d.message);
					} else {
						dispatch(ASSETS_CRUD_ACTION_CREATORS.updateSuccess(d.data));
						resolve(d.data);
					}
				})
				.catch((d) => {
					dispatch(ASSETS_CRUD_ACTION_CREATORS.createError(d.message, entity));
					reject(d.message);
				});
		});
	};
};

export const moveAssetToLocationForBuyer = moveAssetToLocation(ROLE_TYPES.BUYER);
export const moveAssetToLocationForSupplier = moveAssetToLocation(ROLE_TYPES.SUPPLIER);

const createMultipleAssetsAsDeleted = (roleType) => (entities) => {
	const baseUrl = getBackendUri();
	const apiUrl = `/api/v1/${roleType}/asset/assets/create_multiple_as_deleted`;

	// if sorting present, fix order naming and add to query params

	return (dispatch) => {
		const entity = {};
		return new Promise((resolve, reject) => {
			dispatch(ASSETS_CRUD_ACTION_CREATORS.updateStart(entity));

			fetchUtils
				.post(
					baseUrl + apiUrl,
					Object.assign({
						headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
						data: entities,
					})
				)
				.then(parseJSON)
				.then(({ json, status, ok }) => {
					const d = json;
					if (status === 401) {
						dispatch(logoutSuccess());
					}
					if (d.status === 'error') {
						dispatch(ASSETS_CRUD_ACTION_CREATORS.updateError(d.message, entity));
						reject(d.message);
					} else {
						dispatch(ASSETS_CRUD_ACTION_CREATORS.updateSuccess(d.data));
						resolve(d.data);
					}
				})
				.catch((d) => {
					dispatch(ASSETS_CRUD_ACTION_CREATORS.createError(d.message, entity));
					reject(d.message);
				});
		});
	};
};

export const createMultipleAssetsAsDeletedForSupplier = createMultipleAssetsAsDeleted(
	ROLE_TYPES.SUPPLIER
);

const getAllAssetsForBulkUpdate =
	(roleType) =>
	(params = {}) => {
		const baseUrl = getBackendUri();
		const apiUrl = `/api/v1/${roleType}/asset/assets/lite/bulk_transfers`;

		return (dispatch) => {
			return new Promise((resolve, reject) => {
				fetchUtils
					.get(
						baseUrl + apiUrl,
						Object.assign(
							{ headers: { Accept: 'application/json', 'Content-Type': 'application/json' } },
							params
						)
					)
					.then(parseJSON)
					.then(({ json, status, ok }) => {
						const d = json;
						if (status === 401) {
							dispatch(logoutSuccess());
						}
						if (d.status === 'error') {
							reject(d.message);
							message.error(d.data);
						} else {
							resolve(d.data);
						}
					})
					.catch((d) => {
						reject(d.message);
					});
			});
		};
	};

export const getAllAssetsForBulkUpdateForSupplier = getAllAssetsForBulkUpdate(ROLE_TYPES.SUPPLIER);

/**
 * The OpenSearch-backed LITE asset search, for filter dropdowns.
 *
 * `assetsRestCrudThunks.readLite` hits `GET /asset/assets/lite`, which is the raw-SQL listing path.
 * This one POSTs `/asset/assets/search/lite`, so a filter's type-to-search reaches the same index the
 * asset screens read and matches the same fields.
 *
 * The load-bearing detail is which action it dispatches. The `search*` thunks dispatch
 * `fetchSearchSuccess`, which stores entities under `stateSlice.searchRecords` — but every dropdown
 * reads them through `getRecordsForTargetCollection`, which resolves `recordsIds` against
 * `stateSlice.records`. So a dropdown fed by a search thunk renders EMPTY even though the request
 * succeeded. This dispatches the ordinary `fetchSuccess` instead, which is safe precisely because the
 * lite search already returns whole `AssetLite` objects: there is nothing left to hydrate, so nothing
 * is lost by treating them as records.
 *
 * POST, not GET, for the same reason the asset index search is: the filter bag can carry long id lists.
 */
const searchAssetsLite =
	(roleType) =>
	(
		params,
		targetCollectionName = null,
		pagination = null,
		sorting = null,
		filters = null,
		addToTargetCollection = false
	) => {
		const queryParams = {
			...(pagination
				? { offset: (pagination.current - 1) * pagination.pageSize, limit: pagination.pageSize }
				: {}),
			...(sorting ? { ...sorting, order: sorting.order === 'ascend' ? 'asc' : 'desc' } : {}),
		};
		const queryString = Object.keys(queryParams)
			.map((key) => `${key}=${encodeURIComponent(queryParams[key])}`)
			.join('&');

		return (dispatch) =>
			new Promise((resolve, reject) => {
				dispatch(ASSETS_CRUD_ACTION_CREATORS.fetchStart(targetCollectionName));

				fetchUtils
					.post(
						`${getBackendUri()}/api/v1/${roleType}/asset/assets/search/lite${
							queryString ? `?${queryString}` : ''
						}`,
						{
							headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
							data: { ...(params || {}), ...(filters || {}) },
						}
					)
					.then(parseJSON)
					.then(({ json, status }) => {
						if (status === 401) {
							dispatch(logoutSuccess());
							reject('unauthorized');
							return;
						}
						if (nullSafeGet('status', json) === 'error') {
							// `targetCollectionName`, not '': fetchStart above set `fetching` on the NAMED
							// collection, and the reducer only clears the flag it is told about. Passing ''
							// clears the root `assets.fetching` and leaves the caller's collection fetching
							// forever — the asset filter's async select then spins for the rest of the session.
							dispatch(
								ASSETS_CRUD_ACTION_CREATORS.fetchError(
									nullSafeGet('message', json),
									targetCollectionName
								)
							);
							reject(nullSafeGet('message', json));
							return;
						}
						const records = nullSafeGetOrElse('data', json, []);
						const count = nullSafeGetOrElse('count', json, records.length);
						dispatch(
							ASSETS_CRUD_ACTION_CREATORS.fetchSuccess(
								records,
								count,
								targetCollectionName,
								{ ...pagination, total: count },
								sorting,
								filters,
								addToTargetCollection
							)
						);
						resolve(records);
					})
					.catch((error) => {
						dispatch(
							ASSETS_CRUD_ACTION_CREATORS.fetchError(
								nullSafeGet('message', error),
								targetCollectionName
							)
						);
						reject(nullSafeGet('message', error));
					});
			});
	};

export const searchAssetsLiteForBuyer = searchAssetsLite(ROLE_TYPES.BUYER);
export const searchAssetsLiteForSupplier = searchAssetsLite(ROLE_TYPES.SUPPLIER);
export const searchAssetsLiteForRole = (roleType) =>
	roleType === ROLE_TYPES.SUPPLIER ? searchAssetsLiteForSupplier : searchAssetsLiteForBuyer;

/**
 * Partial update of one asset — `PATCH /asset/assets/:id`.
 *
 * Its backend (`AssetController.constructAssetToPatchAsset`, shared by the buyer and supplier
 * controllers) reads a fixed whitelist of keys and applies each as `given.orElse(existing)`. Three
 * consequences worth knowing before reaching for this over `update`:
 *
 *   - An omitted key is left alone, which is the point: no read-modify-write, so a concurrent edit
 *     to a field you did not send cannot be clobbered.
 *   - A field can be set but never cleared — sending nothing and sending null are the same thing.
 *   - A key outside the whitelist is silently dropped, with a 200 and the unchanged record back.
 *     Today the whitelist is externalSpend, installDate, lifeExpectancyInMonths, originalPrice,
 *     capitalisedCost, the two userOverrideReplacementRecommendation fields, and (added by
 *     openwrench-app#2425) serialNumber, modelNumber and manufacturer.
 *
 * So a general asset edit still belongs on the PUT that `update` wraps. This is for changing a few
 * named fields and nothing else.
 */
export const patchAsset = (roleType) => (id, updates) => {
	const apiUrl = `/api/v1/${roleType}/asset/assets/${id}`;
	return (dispatch) => {
		return new Promise((resolve, reject) => {
			dispatch(ASSETS_CRUD_ACTION_CREATORS.updateStart(updates));

			fetchUtils
				.patch(getBackendUri() + apiUrl, {
					headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
					data: updates,
				})
				.then(parseJSON)
				.then(({ json, status }) => {
					if (status === 401) {
						dispatch(logoutSuccess());
					}
					if (nullSafeGet('status', json) === 'error') {
						dispatch(
							ASSETS_CRUD_ACTION_CREATORS.updateError(nullSafeGet('message', json), updates)
						);
						reject(nullSafeGet('message', json));
						return;
					}
					const record = nullSafeGet('data', json);
					dispatch(ASSETS_CRUD_ACTION_CREATORS.updateSuccess(record));
					resolve(record);
				})
				.catch((error) => {
					dispatch(ASSETS_CRUD_ACTION_CREATORS.updateError(nullSafeGet('message', error), updates));
					reject(nullSafeGet('message', error));
				});
		});
	};
};
